import { useSelection } from '../../hooks/PRIVATE_useVisSelection';
import type { DiagramNode, DiagramLink, ItemInfo } from '../Diagram.types';
import { getNavigableInfo } from '../utils/diagramUtils';
import { Ref } from 'preact/hooks';

type SelectionProps<K1,K2> = {
    selectedIds: {
			nodes?: K1[],
			links?: K2[]
		},
    selectionMode: 'none'|'single'|'multiple';
		nodeMap: {
			itemIdToDataMap: Map<K1, DiagramNode<K1>>,
			itemIdToIndexMap: Map<K1, number>
			itemIndexToIdMap: Map<number, K1>
		},
		linkMap: {
			itemIdToDataMap: Map<K1, DiagramLink<K2, K1>>,
			itemIdToIndexMap: Map<K2, number>
			itemIndexToIdMap: Map<number, K2>
		}
		focusedItemInfo: ItemInfo<K1,K2>,
    onSelectionChange?: (detail: { nodes: Array<K1>; links: Array<K2> }) => void,
		rootRef: Ref<HTMLDivElement>
  };

type EncodedNodeId = `N_${number}`;
type EncodedLinkId = `L_${number}`;

export function useDiagramSelection<K1,K2> ({
selectedIds,
selectionMode,
nodeMap,
linkMap,
focusedItemInfo,
onSelectionChange,
rootRef
}: SelectionProps<K1,K2>) {
	const nodeIdToIndexMap = nodeMap.itemIdToIndexMap;
	const nodeIndexToIdMap = nodeMap.itemIndexToIdMap;
	const linkIdToIndexMap = linkMap.itemIdToIndexMap;
	const linkIndexToIdMap = linkMap.itemIndexToIdMap;
	const encodeNodeId = (id: K1) => `N_${nodeIdToIndexMap.get(id)}` as EncodedNodeId;
	const decodeNodeId = (id: EncodedNodeId) => nodeIndexToIdMap.get(Number(id.slice(2)));
	const encodeLinkId = (id: K2) => `L_${linkIdToIndexMap.get(id)}` as EncodedLinkId;
	const decodeLinkId = (id: EncodedLinkId) => linkIndexToIdMap.get(Number(id.slice(2)));

	const encodedNodesSelection = selectedIds.nodes ? selectedIds.nodes.map(encodeNodeId) : [];
	const encodedLinksSelection = selectedIds.links ? selectedIds.links.map(encodeLinkId) : [];
	const encodedSelection = [...encodedNodesSelection, ...encodedLinksSelection]
	const idExtracter = (event: Event) => {
		// return encodeNodeId(id) or encodeLinkId(id) depending on node or link selection
		const info =
		event.type === 'keyup'
			? focusedItemInfo
			: getNavigableInfo<K1,K2>(event.target as HTMLElement, rootRef);
		return info.isNode ? encodeNodeId(info.id as K1) : encodeLinkId(info.id as K2);
	};

	const onEncodedSelectionChange = (detail: { ids: (EncodedNodeId | EncodedLinkId)[] }) => {
		const newSelectedNodeIds = detail.ids.filter((encId) => encId[0] === 'N').map((encId) => decodeNodeId(encId as EncodedNodeId)!);
		const newSelectedLinkIds = detail.ids.filter((encId) => encId[0] === 'L').map((encId) => decodeLinkId(encId as EncodedLinkId)!);
		return onSelectionChange && onSelectionChange({ nodes: newSelectedNodeIds, links: newSelectedLinkIds });
	};

	const selectionProps = useSelection({
		selection: encodedSelection,
		idExtracter,
		selectionMode,
		onChange: onEncodedSelectionChange
	})

return selectionProps
}